import axios from "axios";
import { isInsufficientBalanceError } from "@/shared/lib/insufficientBalanceError";
import { requestOpenTopUpModal } from "@/shared/lib/topUpModalBridge";
import { getAnyInitData, getInitDataHeaderName, isMiniAppContext, } from "@/shared/lib/miniApp";
import { getSessionId } from "@/shared/lib/analyticsSession";
import { safeLocalStorage } from "@/shared/lib/safeStorage";
import { refreshAccessToken } from "./auth";
// Флаг для отслеживания процесса обновления токена
let isRefreshing = false;
// Очередь запросов, которые ждут обновления токена
let failedQueue = [];
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        }
        else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};
export const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "",
    headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Headers": "*",
    },
    timeout: 85000,
    withCredentials: true,
});
api.interceptors.request.use((config) => {
    // Проверяем, находимся ли мы в мини-приложении (Telegram или MAX)
    const isMiniApp = isMiniAppContext();
    // Для мини-приложения используем токены из localStorage
    // Для веб-версии токены в куках, не добавляем Authorization заголовок.
    // safeLocalStorage: на iOS Private Mode getItem бросает SecurityError
    // («The operation is insecure») — без обёртки падает весь axios-запрос.
    if (isMiniApp) {
        const token = safeLocalStorage.getItem("accessToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    // initData — кладём в заголовок на КАЖДЫЙ запрос, пока он доступен.
    // Имя заголовка зависит от платформы: Telegram → X-Telegram-Init-Data,
    // MAX → X-Max-Init-Data (так бэкенд различает источник подписи). Без этого
    // /me и /refresh из MAX уходили без заголовка → 401.
    // getAnyInitData берёт подписанную строку с моста платформы
    // (Telegram → window.Telegram.WebApp, MAX → window.WebApp.initData).
    const initData = getAnyInitData();
    if (initData) {
        config.headers[getInitDataHeaderName()] = initData;
    }
    // X-Session-Id на КАЖДЫЙ запрос (не только аналитику): по нему бэк
    // привязывает свои события (text_generated, song_generated, login, …) к той
    // же продуктовой сессии и сохраняет session_id на сущностях. Заодно продлевает
    // окно сессии (touch внутри getSessionId).
    try {
        config.headers["X-Session-Id"] = getSessionId();
    }
    catch {
        /* не блокируем запрос, если сессию не удалось получить */
    }
    // Убеждаемся, что withCredentials включен для всех запросов
    config.withCredentials = true;
    return config;
}, (error) => {
    return Promise.reject(error);
});
api.interceptors.response.use((response) => {
    return response;
}, async (error) => {
    const originalRequest = error.config;
    // Проверяем, находимся ли мы в мини-приложении (Telegram или MAX)
    const isMiniApp = isMiniAppContext();
    if (error.response?.status === 401 && originalRequest) {
        // Если это запрос на refresh или logout, не пытаемся обновить токен
        if (originalRequest.url?.includes("/auth/refresh") ||
            originalRequest.url?.includes("/auth/logout") ||
            originalRequest.url?.includes("/auth/verify-code")) {
            // Для мини-приложения не удаляем токены сразу
            if (!isMiniApp) {
                console.error("Unauthorized on auth endpoint");
                safeLocalStorage.removeItem("accessToken");
                safeLocalStorage.removeItem("refreshToken");
            }
            return Promise.reject(error);
        }
        // Если уже пытаемся обновить токен, добавляем запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => {
                // Повторяем оригинальный запрос после обновления токена
                return api(originalRequest);
            })
                .catch((err) => {
                return Promise.reject(err);
            });
        }
        // Если запрос уже был повторен, не пытаемся снова
        if (originalRequest._retry) {
            // Для мини-приложения не удаляем токены сразу
            if (!isMiniApp) {
                console.error("Unauthorized after retry");
                safeLocalStorage.removeItem("accessToken");
                safeLocalStorage.removeItem("refreshToken");
            }
            return Promise.reject(error);
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
            // Пытаемся обновить токен
            await refreshAccessToken();
            // Успешно обновили токен, обрабатываем очередь
            processQueue(null, null);
            // Повторяем оригинальный запрос
            return api(originalRequest);
        }
        catch (refreshError) {
            // Не удалось обновить токен
            processQueue(refreshError, null);
            // Для мини-приложения не удаляем токены сразу
            if (!isMiniApp) {
                console.error("Failed to refresh token");
                safeLocalStorage.removeItem("accessToken");
                safeLocalStorage.removeItem("refreshToken");
            }
            return Promise.reject(refreshError);
        }
        finally {
            isRefreshing = false;
        }
    }
    if (isInsufficientBalanceError(error)) {
        requestOpenTopUpModal();
    }
    return Promise.reject(error);
});
export const setAuthToken = (token) => {
    // Проверяем, находимся ли мы в мини-приложении (Telegram или MAX)
    const isMiniApp = isMiniAppContext();
    if (token) {
        // Для мини-приложения сохраняем токен в localStorage и заголовок
        if (isMiniApp) {
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            safeLocalStorage.setItem("accessToken", token);
        }
        // Для веб-версии токены в куках, не нужно ничего делать
    }
    else {
        // Очищаем токен
        if (isMiniApp) {
            delete api.defaults.headers.common["Authorization"];
            safeLocalStorage.removeItem("accessToken");
        }
        // Для веб-версии куки удаляются на сервере при logout
    }
};
